/**
 * multiAgentIdeStore — the IDE-side state for the MULTI-AGENT run.
 *
 * SHARED CONTRACT. This store deliberately MIRRORS the slice SHAPES that the
 * dev-agent right-panels consume (DependencySnapshot, TestRun, coveredClasses,
 * coverageLines) so that dev-agent panel/editor components can be COPIED into
 * `pages/multi-agent/ide/` and made multi-agent-native by a single swap:
 *
 *     useDevAgentStore  ->  useMultiAgentIdeStore
 *
 * The difference is the FEED: every slice here is populated by the MULTI-AGENT
 * run (useRunStream SSE + the multi-agent run-test endpoint), NEVER by the
 * dev-agent session. Where data is more naturally owned by `useMultiAgentStore`
 * (chat/thread/artifacts), this store DERIVES from it rather than duplicating.
 *
 * HARD RULE (per the task): nothing under pages/dev-agent or devAgentStore.ts is
 * touched. The TYPE shapes below are re-declared here (not imported from the
 * dev-agent store) so this store has no dependency edge into dev-agent code —
 * but they are kept structurally identical so a copied panel type-checks.
 */
import { create } from "zustand";
import { registerOrgScopedReset } from "@/lib/org-scope";
import {
  listRecentRuns,
  runMultiAgentTest,
  collapseWhitespace,
  type MultiAgentCoveredClass,
} from "@/services/multi-agent";
import { useMultiAgentStore, readPersistedMultiAgentSession } from "@/stores/multiAgentStore";
import { bareClassName } from "../utils/bareClassName";

// ── Shared shapes (structurally identical to the dev-agent store) ──────────

/** mirror of devAgentStore.types `AgentRunStatus` */
export type AgentRunStatus = "idle" | "running" | "completed" | "failed";

/** mirror of DevAgentDependencyItem */
export interface DependencyItem {
  name: string;
  id: string;
  metadata_type: string;
}

/** mirror of DevAgentDependencySnapshot — the shape DependenciesPanel renders. */
export interface DependencySnapshot {
  target: string;
  target_id?: string | null;
  callers: DependencyItem[];
  tests: DependencyItem[];
  total: number;
  status: "ready" | "awaiting_user_scope" | "approved" | "rejected" | "applied";
  captured_at: string;
}

/** mirror of the `coveredClasses` entry shape used by TestsPanel and run-test. */
export type CoveredClass = MultiAgentCoveredClass;

/** mirror of devAgentStore.types `TestRunEntry` — the shape TestsPanel reads. */
export interface TestRun {
  testClassName: string;
  className: string | null;
  coveredClasses: CoveredClass[];
  success: boolean;
  summary: string;
  rawResult?: string;
  passed?: number;
  failed?: number;
  total?: number;
  ts: number;
}

/** mirror of DevAgentCoverageLines — drives Monaco line decorations. */
export interface CoverageLines {
  className: string;
  covered_lines: number[];
  uncovered_lines: number[];
  coverage_pct: number | null;
}

/** Impact-analysis snapshot for a saved class. Shape mirrors the dev-agent
 *  `impact_analysis` FeedItem.data so the copied ImpactPanel renders unchanged.
 *  Fed by the multi-agent post-save analyze endpoint + `impact_analysis` SSE. */
export interface ImpactAnalysisEntry {
  class_name: string;
  blast_radius?: number;
  callers?: Array<{ name: string; type: string; risk: "high" | "medium" | "low" }>;
  callees?: Array<{ name: string; type: string }>;
  data_access?: Array<{ object: string; operations: string[] }>;
  triggers_at_risk?: Array<{ trigger: string; object: string }>;
  references?: Array<{ file: string; line: number; kind: string }>;
  summary?: string;
}

/** Aggregate test-coverage snapshot for a saved class. Shape mirrors the
 *  dev-agent `test_coverage` FeedItem.data. Fed by the analyze endpoint +
 *  `test_coverage` SSE; surfaced in TestsPanel as the aggregate row. */
export interface TestCoverageEntry {
  class_name: string;
  test_classes?: Array<{
    name: string;
    coverage_pct: number | null;
    lines_covered?: number | null;
    lines_uncovered?: number | null;
  }>;
  aggregate_coverage_pct?: number | null;
  summary?: string;
}

/** Artifact row surfaced to IDE panels. Derived from useMultiAgentStore.artifacts,
 *  enriched with the per-artifact R/C/M badge and the source_artifact_id link
 *  (the UUID of the original source artifact this one was derived from). */
export interface IdeArtifact {
  id: string;
  type: string;
  name: string;
  toolName: string;
  byteSize: number;
  createdAt: number;
  /** UUID of the source artifact this was derived from (artifact_created.metadata.source_artifact_id). */
  sourceArtifactId?: string | null;
  /** R=read from org, C=created/generated, M=modified */
  badge?: "R" | "C" | "M";
}

/** created/modified mirror emit_code's `operation`; "deleted" means the
 *  accepted diff has no new body BY DESIGN (a destructiveChanges removal),
 *  not an absent/empty diff — showDiff and handleSave both key off this so
 *  an empty replacement string is never mistaken for "nothing pending". */
export type AcceptedCodeOperation = "created" | "modified" | "deleted";

export interface WorkspaceCodeRequest {
  id: string;
  name: string;
  metadataType: string;
  content: string;
  badge: "R" | "C" | "M";
  acceptedCode?: string | null;
  acceptedCodeOriginal?: string | null;
  acceptedCodeOperation?: AcceptedCodeOperation | null;
}

// ── Store data ─────────────────────────────────────────────────────────────

interface MultiAgentIdeStoreData {
  /** Dep-analysis snapshots, newest-upserted, capped. Fed by `dependencies_ready` SSE. */
  dependencies: DependencySnapshot[];
  /** User/agent test runs. Fed by `test_run_completed` SSE + runTest(). */
  testRuns: TestRun[];
  /** Test currently executing (panel shows a spinner + Stop). */
  runningTest: { testClassName: string; startedAt: number } | null;
  /** Line-level coverage for the active editor file (Monaco decorations). */
  coverageLines: CoverageLines | null;
  /** Post-save impact-analysis snapshots, newest-upserted by class, capped.
      Fed by the analyze endpoint result + `impact_analysis` SSE. */
  impactAnalysis: ImpactAnalysisEntry[];
  /** Post-save aggregate coverage snapshots, newest-upserted by class, capped.
      Fed by the analyze endpoint result + `test_coverage` SSE. */
  testCoverageData: TestCoverageEntry[];
  /** Mirror of useMultiAgentStore.status mapped to dev-agent AgentRunStatus. */
  runStatus: AgentRunStatus;
  /** Bumped on each "New session" click so the IDE panel clears the editor's open
   *  files (they belong to the prior session) — without that, the editor's
   *  metadata query keeps firing at a dead id and 404s during the transition. */
  newSessionNonce: number;
  /** Per-artifact R/C/M badge keyed by artifact id. */
  artifactBadges: Record<string, "R" | "C" | "M">;
  /** Per-artifact source_artifact_id keyed by artifact id (from artifact_created). */
  sourceArtifactIds: Record<string, string>;
  /** Class selected in the explorer to refactor — its UUID + name + source is
      seeded into the next multi-agent run so the agent targets that exact class. */
  pendingRefactorFile: { id: string; name: string; content: string; metadataType?: string } | null;
  /** Bare class name (no .cls/.trigger) of the file currently focused in the
      editor. Lets the SSE test_run_completed consumer + runTest pick the matching
      coveredClass and light Monaco gutter coverage (MA-14). */
  activeEditorClassName: string | null;
  /** Tab id the current `acceptedCode` belongs to (MA-17). showDiff is gated on
      this matching the active file so a stale acceptedCode never opens the diff
      on the wrong tab. */
  acceptedCodeTarget: string | null;
  /** In-hand ORIGINAL (left side of Compare) for a refactor *diff artifact* whose
      before/after lives INSIDE the artifact content (fixes[].source.original) —
      its source_artifact_id points at the blast-radius JSON, not the real class,
      so Compare must use this directly instead of resolving sourceArtifactIds. */
  acceptedCodeOriginal: string | null;
  /** What KIND of change the current acceptedCode represents (created/modified/
      deleted) — see AcceptedCodeOperation. Matched to acceptedCodeTarget the
      same way acceptedCodeOriginal is. */
  acceptedCodeOperation: AcceptedCodeOperation | null;
  /** Cross-panel command: generated-code and impact/dependency surfaces can ask
      the IDE to open an exact workspace tab and optionally prepare Compare. */
  workspaceCodeRequest: WorkspaceCodeRequest | null;
}

interface MultiAgentIdeStoreActions {
  // dependencies
  setDependencies: (deps: DependencySnapshot[]) => void;
  addDependency: (dep: DependencySnapshot) => void;
  clearDependencies: () => void;
  // tests
  addTestRun: (run: TestRun) => void;
  setRunningTest: (running: { testClassName: string; startedAt: number } | null) => void;
  clearTestRuns: () => void;
  /** Run a test against the org via the multi-agent run-test endpoint. */
  /** opts.announceStart=false suppresses the "Running X…" start toast — used by the
      post-save AUTO test runs so background activity stays quiet (user decision
      2026-08-14); pass/fail toasts show either way, as they always did. */
  runTest: (testClassName: string, opts?: { announceStart?: boolean }) => Promise<void>;
  stopTest: () => void;
  // coverage
  setCoverageLines: (
    className: string,
    covered: number[],
    uncovered: number[],
    pct: number | null,
  ) => void;
  clearCoverageLines: () => void;
  // post-save impact analysis
  addImpactAnalysis: (entry: ImpactAnalysisEntry) => void;
  clearImpactAnalysis: () => void;
  // post-save aggregate coverage
  addTestCoverage: (entry: TestCoverageEntry) => void;
  clearTestCoverage: () => void;
  // run status mirror
  setRunStatus: (s: AgentRunStatus) => void;
  // artifact enrichment
  setArtifactBadge: (id: string, badge: "R" | "C" | "M") => void;
  setSourceArtifactId: (id: string, sourceArtifactId: string) => void;
  // refactor-target selection (class UUID -> next run)
  setPendingRefactorFile: (
    f: { id: string; name: string; content: string; metadataType?: string } | null,
  ) => void;
  // active editor file (for coverage-decoration matching)
  setActiveEditorClassName: (className: string | null) => void;
  // accepted-code target tab (MA-17 showDiff gate)
  setAcceptedCodeTarget: (tabId: string | null) => void;
  // in-hand original for refactor diff artifacts (Compare left side)
  setAcceptedCodeOriginal: (content: string | null) => void;
  // what kind of change the current acceptedCode represents
  setAcceptedCodeOperation: (operation: AcceptedCodeOperation | null) => void;
  setWorkspaceCodeRequest: (request: WorkspaceCodeRequest | null) => void;
  // lifecycle
  bumpNewSession: () => void;
  reset: () => void;
}

export interface MultiAgentIdeStore
  extends MultiAgentIdeStoreData,
    MultiAgentIdeStoreActions {}

const _initial: MultiAgentIdeStoreData = {
  dependencies: [],
  testRuns: [],
  runningTest: null,
  coverageLines: null,
  impactAnalysis: [],
  testCoverageData: [],
  runStatus: "idle",
  artifactBadges: {},
  sourceArtifactIds: {},
  pendingRefactorFile: null,
  activeEditorClassName: null,
  acceptedCodeTarget: null,
  acceptedCodeOriginal: null,
  acceptedCodeOperation: null,
  workspaceCodeRequest: null,
  newSessionNonce: 0,
};

/** Pick the coveredClass whose name matches the active editor file, so Monaco
 *  gutter coverage lights up for the file the user is looking at. Falls back to
 *  the run's production/first class when nothing matches. */
function pickCoverageForActiveEditor(
  coveredClasses: CoveredClass[],
  activeEditorClassName: string | null,
): CoveredClass | null {
  if (!coveredClasses.length) return null;
  const target = bareClassName(activeEditorClassName);
  if (target) {
    const match = coveredClasses.find((c) => bareClassName(c.name) === target);
    if (match) return match;
  }
  return null;
}

export const useMultiAgentIdeStore = create<MultiAgentIdeStore>()((set, get) => ({
  ..._initial,

  // ── dependencies (shape + semantics == dev-agent) ───────────────────────
  setDependencies: (dependencies) =>
    set({
      dependencies: Array.isArray(dependencies)
        ? dependencies
        : dependencies
          ? [dependencies as DependencySnapshot]
          : [],
    }),
  addDependency: (dep) =>
    set((s) => {
      // Upsert by target (latest snapshot wins), cap at 30 like dev-agent.
      const filtered = (s.dependencies || []).filter((d) => d.target !== dep.target);
      return { dependencies: [...filtered, dep].slice(-30) };
    }),
  clearDependencies: () => set({ dependencies: [] }),

  // ── tests (shape + semantics == dev-agent) ──────────────────────────────
  addTestRun: (run) =>
    set((s) => ({
      testRuns: [
        ...s.testRuns.filter((t) => t.testClassName !== run.testClassName),
        run,
      ].slice(-20),
    })),
  setRunningTest: (runningTest) => set({ runningTest }),
  clearTestRuns: () => set({ testRuns: [] }),
  stopTest: () => set({ runningTest: null }),

  runTest: async (testClassName: string, opts?: { announceStart?: boolean }) => {
    // The run-id precondition lives HERE, once — callers must not duplicate it
    // (TestsPanel's copy drifted to live-only and the editor-toolbar path had no
    // feedback at all: a dead click). The backend's /run-test uses the run id
    // ONLY as an ownership/org-context anchor (multi_agent_runs WHERE id+user+org
    // — no liveness/status check), so ANY run of this user in this org works:
    // live id → persisted session id → the most recent run from history. Only a
    // user with NO run ever in this org still needs to start one.
    let runId = useMultiAgentStore.getState().runId
      || readPersistedMultiAgentSession().runId;
    const { default: toast } = await import("react-hot-toast");
    if (!runId) {
      // Reuse the most recent run from history: /run-test only needs the run as an
      // ownership/org anchor, so any past run works — first-ever users still get
      // the instructional toast (never a swallowed click, per 2de2e1ae).
      try {
        const { runs } = await listRecentRuns();
        runId = runs?.[0]?.id;
      } catch { /* history unavailable → fall through to the instructional toast */ }
    }
    if (!runId) {
      toast.error(
        "Tests run inside a Weave session — send any message in the chat to start one, then retry.",
      );
      return;
    }
    if (get().runningTest) {
      toast.error("A test is already running");
      return;
    }
    set({ runningTest: { testClassName, startedAt: Date.now() } });
    const isStillActive = () => get().runningTest?.testClassName === testClassName;
    if (opts?.announceStart !== false) {
      toast(`Running ${testClassName}...`);
    }
    try {
      const r = await runMultiAgentTest(runId, { test_class: testClassName });
      if (!isStillActive()) return;
      const success = r.status === "passed" || r.status === "Passed";
      const entry: TestRun = {
        testClassName,
        className: r.className ?? null,
        coveredClasses: r.coveredClasses ?? [],
        success,
        summary: success ? "Tests passed" : `Tests ${r.status || "failed"}`,
        ts: Date.now(),
      };
      get().addTestRun(entry);
      // MA-14: if this run covered the file currently in the editor, light up
      // the Monaco gutter coverage for it.
      const covForEditor = pickCoverageForActiveEditor(
        entry.coveredClasses,
        get().activeEditorClassName,
      );
      if (covForEditor) {
        get().setCoverageLines(
          covForEditor.name,
          covForEditor.covered_lines || [],
          covForEditor.uncovered_lines || [],
          covForEditor.coverage_pct,
        );
      }
      if (success) {
        toast.success(`${testClassName}: passed (${r.coveragePct ?? 0}%)`);
      } else {
        toast.error(`${testClassName}: ${r.status || "failed"}`);
      }
    } catch (err) {
      if (!isStillActive()) return;
      const timedOut = err instanceof Error && /timed out/i.test(err.message);
      get().addTestRun({
        testClassName,
        className: null,
        coveredClasses: [],
        success: false,
        summary: timedOut ? "Test run timed out" : "Test execution failed",
        ts: Date.now(),
      });
      toast.error(
        timedOut
          ? `${testClassName}: test run timed out — the org may still be running it`
          : `${testClassName}: failed to run`,
      );
    } finally {
      if (get().runningTest?.testClassName === testClassName) {
        set({ runningTest: null });
      }
    }
  },

  // ── coverage lines (shape + semantics == dev-agent) ─────────────────────
  setCoverageLines: (className, covered_lines, uncovered_lines, coverage_pct) =>
    set({ coverageLines: { className, covered_lines, uncovered_lines, coverage_pct } }),
  clearCoverageLines: () => set({ coverageLines: null }),

  // ── post-save impact analysis (shape == dev-agent impact FeedItem) ──────
  addImpactAnalysis: (entry) =>
    set((s) => {
      const filtered = (s.impactAnalysis || []).filter(
        (e) => e.class_name !== entry.class_name,
      );
      return { impactAnalysis: [...filtered, entry].slice(-30) };
    }),
  clearImpactAnalysis: () => set({ impactAnalysis: [] }),

  // ── post-save aggregate coverage (shape == dev-agent test_coverage FeedItem) ──
  addTestCoverage: (entry) =>
    set((s) => {
      const filtered = (s.testCoverageData || []).filter(
        (e) => e.class_name !== entry.class_name,
      );
      return { testCoverageData: [...filtered, entry].slice(-30) };
    }),
  clearTestCoverage: () => set({ testCoverageData: [] }),

  // ── run status mirror ───────────────────────────────────────────────────
  setRunStatus: (runStatus) => set({ runStatus }),

  // ── artifact enrichment ─────────────────────────────────────────────────
  setArtifactBadge: (id, badge) =>
    set((s) => ({ artifactBadges: { ...s.artifactBadges, [id]: badge } })),
  setSourceArtifactId: (id, sourceArtifactId) =>
    set((s) =>
      s.sourceArtifactIds[id] === sourceArtifactId
        ? s
        : { sourceArtifactIds: { ...s.sourceArtifactIds, [id]: sourceArtifactId } },
    ),

  setPendingRefactorFile: (pendingRefactorFile) => set({ pendingRefactorFile }),

  setActiveEditorClassName: (activeEditorClassName) => set({ activeEditorClassName }),

  setAcceptedCodeTarget: (acceptedCodeTarget) => set({ acceptedCodeTarget }),

  setAcceptedCodeOriginal: (acceptedCodeOriginal) => set({ acceptedCodeOriginal }),
  setAcceptedCodeOperation: (acceptedCodeOperation) => set({ acceptedCodeOperation }),

  setWorkspaceCodeRequest: (workspaceCodeRequest) => set({ workspaceCodeRequest }),

  bumpNewSession: () => set((s) => ({ newSessionNonce: s.newSessionNonce + 1 })),

  // Preserve newSessionNonce across reset() — reset fires on every send (the
  // sessionId flicker), and only the "New session" click should bump the nonce.
  reset: () => set((s) => ({ ..._initial, newSessionNonce: s.newSessionNonce })),
}));

// ── Derivations from useMultiAgentStore ──────────────────────────────────────
// These are exposed as plain selectors (not store state) so the multi-agent run
// remains the single source of truth for chat/artifacts and we never duplicate
// it. Components call these instead of holding a second copy.

/** Map useMultiAgentStore.status -> dev-agent AgentRunStatus for the IDE shell. */
export function mapRunStatus(
  s: ReturnType<typeof useMultiAgentStore.getState>["status"],
): AgentRunStatus {
  switch (s) {
    case "starting":
    case "planning":
    case "awaiting_approval":
    case "running":
      return "running";
    case "done":
      return "completed";
    case "failed":
    case "cancelled":
      return "failed";
    default:
      return "idle";
  }
}

/**
 * conversationSummary — derived from the multi-agent chat/thread. Each prior
 * turn contributes one bullet (the user intent + a trimmed answer), plus the
 * current turn's user message. Mirrors the dev-agent `conversationSummary:
 * string[]` shape so the Summary panel can render it unchanged.
 */
export function selectConversationSummary(
  s: ReturnType<typeof useMultiAgentStore.getState>,
): string[] {
  const bullets: string[] = [];
  for (const turn of s.thread) {
    const answer = collapseWhitespace(turn.finalMarkdown).slice(0, 160);
    bullets.push(answer ? `${turn.intent} — ${answer}` : turn.intent);
  }
  // current (active) turn
  const currentIntent = s.chat.find((m) => m.role === "user")?.text;
  if (currentIntent && !s.thread.some((t) => t.runId === s.runId)) {
    bullets.push(currentIntent);
  }
  return bullets.slice(-30);
}

/**
 * artifacts — derived from useMultiAgentStore.artifacts, enriched with the IDE
 * store's per-artifact badge + source_artifact_id link. Returns IdeArtifact[].
 */
export function selectIdeArtifacts(): IdeArtifact[] {
  const ma = useMultiAgentStore.getState();
  const ide = useMultiAgentIdeStore.getState();
  return ma.artifacts.map((a) => ({
    ...a,
    sourceArtifactId: ide.sourceArtifactIds[a.id] ?? null,
    badge: ide.artifactBadges[a.id],
  }));
}

// ── runStatus mirror ─────────────────────────────────────────────────────────
// Keep this store's `runStatus` in lock-step with useMultiAgentStore.status so
// the copied IDE shell/panels (which read `runStatus` like the dev-agent) reflect
// the live multi-agent run without any extra wiring in the SSE hook. When the
// multi-agent run resets to idle, also clear the per-run IDE slices.
if (typeof window !== "undefined") {
  let lastStatus: ReturnType<typeof useMultiAgentStore.getState>["status"] | null = null;
  const sync = (status: ReturnType<typeof useMultiAgentStore.getState>["status"]) => {
    if (status === lastStatus) return;
    lastStatus = status;
    useMultiAgentIdeStore.getState().setRunStatus(mapRunStatus(status));
    // Per product decision: do NOT wipe the IDE panels (dependencies / impact /
    // test classes / coverage) on a new run WITHIN a session. They PERSIST across
    // runs so the user keeps context; the new run's SSE upserts entries by class
    // name, refreshing in place. (Cross-SESSION clearing is handled below.)
  };
  // Dependencies / impact / tests / coverage are run-derived and live only in
  // memory, so on a SESSION switch they'd otherwise show the previous session's
  // data. Clear them whenever the active session id changes so each session's
  // panels reflect that session (they repopulate as the session's runs stream).
  let lastSession = useMultiAgentStore.getState().sessionId;
  const onSessionChange = () => {
    const ide = useMultiAgentIdeStore.getState();
    ide.clearDependencies();
    ide.clearImpactAnalysis();
    ide.clearTestRuns();
    ide.clearTestCoverage();
    ide.clearCoverageLines();
  };
  sync(useMultiAgentStore.getState().status);
  useMultiAgentStore.subscribe((s) => {
    sync(s.status);
    if (s.sessionId !== lastSession) {
      lastSession = s.sessionId;
      onSessionChange();
    }
  });
}

// ── Organization scoping ─────────────────────────────────────────────────────
//
// The IDE view holds one organization's files, analysis and editor buffers.
registerOrgScopedReset("multiAgentIdeStore", () => {
  useMultiAgentIdeStore.getState().reset();
});
